import Sonda from '@bit/gerenciatd.utils.front-sonda';
import { VARIANT } from '../components/ProductItem';
import { USER_TYPE } from '../constants/constants';
import { createSondaContext } from './contextUtils';

/**
 * Función que detecta si hay alguna promoción en un grupo de fusión específico
 * @param {object} group
 * @returns {boolean}
 */
export const isPromoInGroup = (group) => {
  let result = false;
  if (!group) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'isPromoInGroup',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'Group no exist' });
    return result;
  }
  Object.keys(group).forEach((productKey) => {
    if (typeof group[productKey] === 'object' && group[productKey].recom) {
      if (group[productKey].recom.promoCode && productKey !== 'categories') {
        result = true;
      }
    }
  });
  return result;
};

/**
 * Obtenemos el producto seleccionado del grupo en concreto
 * @param {object} groupOfProducts
 * @returns {object} Devuelve el producto seleccionado
 * o null en caso de que no exista grupo de products
 */
export const getSelectedProduct = (groupOfProducts) => {
  if (!groupOfProducts) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'getSelectedProduct',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.severalMessage({ info: 'GroupOfProducts no exist' });
    return null;
  }
  const productKeySelected = Object.keys(groupOfProducts).find(
    eachProductKey => groupOfProducts[eachProductKey].selected
  );

  return groupOfProducts[productKeySelected];
};

/**
 * Obtenemos el la promoción más barata que pueda tener un grupo de fusiones
 * @param {object} group
 * @returns {number} Devuelve el producto seleccionado
 * o null en caso de que no exista grupo de products
 */
export const getCheaperPromoPrice = (group) => {
  if (!group) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'getCheaperPromoPrice',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'Group no exist' });
    return null;
  }
  let result = 9999;
  Object.keys(group).forEach((productKey) => {
    if (
      typeof group[productKey] === 'object'
      && group[productKey].recom
      && group[productKey].recom.promoCode
    ) {
      const priceToCompare = parseFloat(group[productKey].dataMap.pvp);
      const resultToCompare = parseFloat(result);
      if (priceToCompare < resultToCompare) {
        result = group[productKey].dataMap.pvp;
      }
    }
  });
  return result;
};

/**
 * Crea el array ordenado por pesos para mostrar la opciones en los selectores
 * @param {Array} optionsArray
 * @returns {Array} Devuelve null en caso de no tener el array de opciones
 */
export const createOptionsArray = (optionsArray) => {
  if (!optionsArray) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'createOptionsArray',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'OptionsArray no exist' });
    return null;
  }
  const result = [];
  optionsArray.forEach((option) => {
    result.push({ value: option, label: option });
  });
  return result;
};

/**
 * Extrae y reestructura la información del commerInfo para mostrarla en el flyout y ficha
 * @param {object} eachCommerInfoBlock
 * @returns {object} Devuleve null en caso de error
 */
export const normalizeCommerInfo = (eachCommerInfoBlock) => {
  if (!eachCommerInfoBlock) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'normalizeCommerInfo',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'eachCommerInfoBlock no existe' });
    return null;
  }
  const result = {
    title: eachCommerInfoBlock.title,
    icon: eachCommerInfoBlock.icon,
  };
  const commerInfoBlockNormalized = eachCommerInfoBlock.commerInfo.map(
    (eachItem) => {
      const hasData = eachItem.claims.some(eachClaim => eachClaim.data);
      const hasBody = eachItem.claims.some(eachClaim => eachClaim.body);
      if (hasBody) {
        return {
          alias: eachItem.alias,
          title: eachItem.title,
          claimWithPills: hasData,
          claims: eachItem.claims.map(eachClaim => (hasData
            ? { body: eachClaim.body, data: eachClaim.data }
            : { body: eachClaim.body })),
        };
      }
      return null;
    }
  );

  const commerInfoBlockFiltered = commerInfoBlockNormalized.filter(
    eachItem => eachItem
  );
  const commerInfoBlockOrdered = commerInfoBlockFiltered.sort(
    (a, b) => a.claimWithPills - b.claimWithPills
  );
  result.info = commerInfoBlockOrdered;
  return result;
};

/**
 * Trata los pesos de las opciones en los selectores
 * @param {string} summaryStr
 * @returns {Array} Array con la info para los selectores, [] en caso de error
 */
export const normalizeSummaryWithWeight = (summaryStr) => {
  if (!summaryStr) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'normalizeSummaryWithWeight',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'summaryStr no existe' });
    return [];
  }
  const summaryReplaced = summaryStr.replaceAll(/•/gi);
  const doc = new DOMParser().parseFromString(
    `<div> ${summaryReplaced} </div>`,
    'text/html'
  );

  return Array.from(doc.querySelectorAll('span'))
    .filter(eachElement => !eachElement.querySelector('.netflix'))
    .map(eachElement => ({
      alias: eachElement.getAttribute('pac-aggr-alias'),
      weight: parseInt(eachElement.getAttribute('pac-aggr-weight'), 10),
      text: eachElement.innerHTML,
    }));
};

/**
 * Trata la info de parque TELCO para adaptarla a lo necesario en la vista
 * @param {object} response
 * @returns {object} Devuelve la info de parque encesaria para pintar el producItem contratado
 * o null en caso de error
 */
export const tratarParqueTELCO = (response) => {
  let codFusionParque = '';
  let codPromoCliente = '';
  const NETFLIX_BUNDLE_PO = '498';
  const NETFLIX_PLAN_ID = '43';
  let tipoNetflix = '';
  const tvClientPOArray = [];

  try {
    if (Array.isArray(response)) {
      const fusion = response.find((element) => {
        return element.productType === 0;
      });
      if (fusion) {
        codFusionParque = fusion.poId;
        // Get promotions
        if (Array.isArray(fusion.promotions) && fusion.promotions.length > 0) {
          fusion.promotions.forEach((promotion) => {
            codPromoCliente += `${promotion.id},`;
          });
        }
        // Get Netflix
        const dependentProducts = fusion.tv.productosContratadosDedependientes;
        if (Array.isArray(dependentProducts) && dependentProducts.length > 0) {
          dependentProducts.forEach((product) => {
            if (product.bundleId === NETFLIX_BUNDLE_PO) {
              if (
                Array.isArray(product.characteristics)
                && product.characteristics.length > 0
              ) {
                product.characteristics.forEach((characteristic) => {
                  if (characteristic.id === NETFLIX_PLAN_ID) {
                    tipoNetflix = `${NETFLIX_BUNDLE_PO}_${characteristic.valueId}`;
                  }
                });
              }
              // Get Netflix promotions
              if (
                Array.isArray(product.promotions)
                && product.promotions.length > 0
              ) {
                product.promotions.forEach((promotion) => {
                  codPromoCliente += `${promotion.id},`;
                });
              }
            } else {
              tvClientPOArray.push(product.poId);
            }
          });
        }
      } else {
        const baf = response.find((element) => {
          return element.productType === 2;
        });
        if (baf) {
          codFusionParque = baf.poId;
          // Get promotions
          if (Array.isArray(baf.promotions) && baf.promotions.length > 0) {
            baf.promotions.forEach((promotion) => {
              codPromoCliente += `${promotion.id},`;
            });
          }
        }
      }
      codFusionParque = tipoNetflix
        ? `${codFusionParque}_${tipoNetflix}`
        : codFusionParque;
      codPromoCliente = codPromoCliente.replace(/,\s*$/, '');
      return { codFusionParque, codPromoCliente, tipoNetflix };
    }
  } catch (error) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'tratarParqueTELCO',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ error });
    return null;
  }
};

export const getPromosInGroupProducts = groupOfProducts => Object.keys(groupOfProducts).reduce((acc, currentProduct) => {
  if (
    currentProduct.includes('fusion')
      && groupOfProducts[currentProduct].recom.promoCode
  ) {
    return acc + 1;
  }
  return acc;
}, 0);

/**
 * Obtiene el producto más barato del grupo
 * @param {object} groupProduct
 * @returns {object} Devuelve el producto más barato de un grupo
 * o null en caso de error
 */
export const getProductLowerPrice = (groupProduct) => {
  if (!groupProduct) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'getProductLowerPrice',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.infoMessage({ info: 'groupProduct no existe' });
  }
  const familyNormalized = Object.keys(groupProduct)
    .filter(eachProductKey => !eachProductKey.includes('visible'))
    .map((productKey) => {
      return {
        productName: productKey,
        price: groupProduct[productKey].dataMap.pvp,
      };
    });

  return familyNormalized.reduce((prev, currentProduct) => (prev.price < currentProduct.price ? prev : currentProduct));
};

/**
 * Chequeamos si hay dobel promo en un producto
 * @param {object} product
 * @returns {boolean} Devuelve si existe promo o no
 * false en caso de error
 */
export const checkHasDoublePromo = (product) => {
  if (
    product
    && product.dataMap
    && product.dataMap.bandaPromo
    && typeof product.dataMap.bandaPromo === 'string'
  ) {
    return product.dataMap.bandaPromo.toLowerCase().includes('doble promo');
  }
  const context = createSondaContext('particulares');
  const sonda = new Sonda({
    processName: 'checkHasDoublePromo',
    context,
    filePath: 'utils/productUtils',
  });
  sonda.errorMessage({ info: 'bandaPromo de product no existe' });
  return false;
};

// CCLI

/**
 * Obteien la promioción del cliente (si la tiene)
 * @param {object} parqueCliente
 * @returns {string} Devuelve losposibles código de pormoción del cliente
 * null en caso de error
 */
export const obtenerPromo = (parqueCliente) => {
  let codigosPromo = '';
  const context = createSondaContext('particulares');
  const sonda = new Sonda({
    processName: 'obtenerPromo',
    context,
    filePath: 'utils/productUtils',
  });
  try {
    // Comprobar que existe del objeto parqueCliente
    if (typeof parqueCliente !== 'undefined' && parqueCliente[0] != null) {
      // Comprobar previamente existencia de parqueBruto
      if (
        typeof parqueCliente.parqueBruto !== 'undefined'
        && parqueCliente[0].parqueBruto != null
      ) {
        parqueCliente.parqueBruto.arbolServicios.SCs.forEach((element) => {
          if (element.listCodPromo !== undefined) {
            element.listCodPromo.forEach((subElement) => {
              if (subElement !== undefined) codigosPromo += `${subElement.attrs[0]};`;
            });
          }
        });
        // Eliminar último separador
        codigosPromo = codigosPromo.substring(0, codigosPromo.length - 1);
      }
    }
    return codigosPromo;
  } catch (error) {
    // Envio error
    sonda.errorMessage({ error });
    return null;
  }
};

/**
 * Obtenemos el código de servicio contratado del parque del cliente
 * @param {object} parqueCliente
 * @returns {string} Devuelve el códgio de servicio contratado
 * null en caso de error
 */
export const obtenerCodigosSc = (parqueCliente) => {
  let codigosSc = '';
  const context = createSondaContext('particulares');
  const sonda = new Sonda({
    processName: 'obtenerCodigosSc',
    context,
    filePath: 'utils/productUtils',
  });
  try {
    // Comprobar que existe del objeto parqueCliente
    if (typeof parqueCliente !== 'undefined' && parqueCliente != null) {
      // Comprobar previamente existencia de parqueBruto
      if (
        typeof parqueCliente.parqueBruto !== 'undefined'
        && parqueCliente.parqueBruto != null
      ) {
        parqueCliente.parqueBruto.arbolServicios.SCs.forEach((element) => {
          if (element !== undefined) {
            // Códigos de Sc no vacíos
            if (element.codSC !== '') {
              // Guardamos código
              codigosSc += `${element.codSC};`;
            }
          }
        });
        // Eliminar último separador
        codigosSc = codigosSc.substring(0, codigosSc.length - 1);
      }
    }
    return codigosSc;
  } catch (error) {
    // Envio error
    sonda.errorMessage({ error });
    return null;
  }
};

/**
 * Obtenemos el parque del cliente legado
 * @param {Array} parqueCliente
 * @returns {objeto} Devuelve la info para identificar el parque del cliente
 * o null en cado de error
 */
export const obtenerParqueCliente = (parqueCliente) => {
  let tipoNetflix = '';
  let codPaqueteParque = '';
  let codCanalParque = '';
  let codFusionParque = '';
  const context = createSondaContext('particulares');
  const sonda = new Sonda({
    processName: 'obtenerParqueCliente',
    context,
    filePath: 'utils/productUtils',
  });
  try {
    // Comprobar posibles valores nulos en la rama
    if (
      parqueCliente
      && parqueCliente.ofertaBruto
      && parqueCliente.ofertaBruto.productos
      && Array.isArray(parqueCliente.ofertaBruto.productos)
    ) {
      parqueCliente.ofertaBruto.productos.forEach((element) => {
        if (
          element.attrs
          && Array.isArray(element.attrs)
          && element.attrs.length > 1
          && element.nombre
        ) {
          element.attrs.forEach((subelement) => {
            if (
              subelement.clave
              && subelement.clave === 'Composicion'
              && subelement.valor
              && Array.isArray(subelement.valor)
              && subelement.valor.length > 0
            ) {
              codFusionParque = subelement.valor[0];
            }
          });

          if (element.attrs[0] && element.attrs[0].valor === 'SC_Parque') {
            if (
              element.attrs[1]
              && element.attrs[1].clave
              && element.attrs[1].clave === 'CodSC'
            ) {
              // Añadir código SC
              if (codPaqueteParque.indexOf(element.attrs[1].valor) === -1) {
                codPaqueteParque += `${element.attrs[1].valor},`;
              }
            }
            // Añadir canal contratado
            if (
              codCanalParque.indexOf(element.attrs[1].valor) === -1
              && element.nombre.indexOf('Parque_TV') > -1
            ) {
              codCanalParque += `${element.attrs[1].valor},`;
            }
          }

          if (
            element.nombre.indexOf('Netflix') > -1
            && element.nombre !== 'Promos_Eliminar_Netflix'
            && element.attrs[3]
            && element.attrs[3].valor
            && Array.isArray(element.attrs[3].valor)
            && element.attrs[3].valor.length > 0
          ) {
            if (parseInt(element.attrs[3].valor[0], 10) === 1) {
              tipoNetflix = 'N2';
            } else if (parseInt(element.attrs[3].valor[0], 10) === 2) {
              tipoNetflix = 'N4';
            }
          }
        }
      });
      // Eliminar última coma
      if (codCanalParque !== '') {
        codCanalParque = codCanalParque.substr(0, codCanalParque.length - 1);
      }
      if (codPaqueteParque !== '') {
        codPaqueteParque = codPaqueteParque.substr(
          0,
          codPaqueteParque.length - 1
        );
      }

      codFusionParque = `${codFusionParque}${tipoNetflix}`;

      return {
        codFusionParque,
        codCanalParque,
        codPaqueteParque,
        tipoNetflix,
      };
    }
    return null;
  } catch (error) {
    // Envio error
    sonda.errorMessage({ error });
    return null;
  }
};

/**
 * Creación de la info necesaria para el taggueado
 * @param {string} tipoNetflix tipo de netflix contratado
 * @param {string} codFusionParque CodIon del fusión del cliente
 * @param {string} codPromoCliente Codigo de pormo si la tiene
 * @param {string} userType Tipo de usuario ( telco o legado )
 * @returns {object} Devuelve el objeto necesario para el taggueado
 * correspondiente al parque del cliente
 */
export const convertirObjetoEnviarParque = (
  tipoNetflix,
  codFusionParque,
  codPromoCliente,
  userType,
  prodPrice = '',
  promotedProdPrice = '',
) => {
  // Crear configCode para envio a Turing
  let configCode = '';
  switch (tipoNetflix) {
    case 'N2':
      configCode = 'modTV=00102512_0OZ_1';
      break;
    case 'N4':
      configCode = 'modTV=00102512_0OZ_2';
      break;
    default:
      configCode = tipoNetflix;
      break;
  }

  const myObject = {
    parqueRecuperado: [
      {
        promoContCode: codPromoCliente, // promos actuales
        configCode, // parametro netflix
        prodPrice,
        promotedProdPrice,
      },
    ],
  };
  let codFusionParqueFinal = codFusionParque;
  if (tipoNetflix) {
    codFusionParqueFinal = codFusionParque.replace(tipoNetflix, '');
  }

  if (userType === USER_TYPE.TELCO || userType === USER_TYPE.GESTOR) {
    if (codFusionParqueFinal[codFusionParqueFinal.length - 1] === '_') {
      codFusionParqueFinal = codFusionParqueFinal.substring(
        0,
        codFusionParqueFinal.length - 1
      );
    }

    myObject.parqueRecuperado[0].prodOffering = codFusionParqueFinal;
  } else {
    myObject.parqueRecuperado[0].prodContCode = codFusionParqueFinal;
  }

  return myObject;
};

/**
 * Tratamos el parque del cliente para obtener los códigos necesarios
 * para identificar dicho parque
 * @param {Array|Object} parqueCliente
 * @returns {Object} Devuelve un objecto con los código de fusion, promo, y netflix
 * o null si hay algún error o el cliente no tiene fijo
 */
export const tratarParqueCCLI = (parqueCliente) => {
  // INIT
  let newParqueCliente = parqueCliente;
  if (parqueCliente) {
    if (Array.isArray(parqueCliente)) {
      newParqueCliente = parqueCliente[0];
    }
    const parqueInfo = obtenerParqueCliente(newParqueCliente);
    const { tipoNetflix, codFusionParque } = parqueInfo;
    const codPromoCliente = obtenerPromo(newParqueCliente);
    if (!codFusionParque || codFusionParque === 'null') {
      return null;
    }
    return { codFusionParque, codPromoCliente, tipoNetflix };
  }
  const context = createSondaContext('particulares');
  const sonda = new Sonda({
    processName: 'tratarParqueCCLI',
    context,
    filePath: 'utils/productUtils',
  });
  sonda.infoMessage({ info: 'Parque cliente no existe' });
  return null;
};

/**
 * Obtenemos el grupo al que pertenece un fusión
 * @param {string} alias
 * @param {Object} allProducts
 * @returns {Object} Devuelve el contenido del grupo al que pertenece el alias y el alaias
 * o null en caso de error
 */
export const getGroupByProductAlias = (alias, allProducts) => {
  if (!allProducts) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'getGroupByProductAlias',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'No existe AllProducts' });
    return null;
  }
  const groupProductsKey = Object.keys(allProducts).find((eachGroupKey) => {
    const groupOfProducts = allProducts[eachGroupKey];
    const result = Object.keys(groupOfProducts)
      .filter(eachProductKey => eachProductKey.includes('fusion'))
      .some(
        eachProductKey => groupOfProducts[eachProductKey].product.alias === alias
      );
    return result || false;
  });
  return { content: allProducts[groupProductsKey], groupProductsKey };
};

/**
 * Normalizamos el orden de los selectores según el peso estáblecido en catálogo
 * @param {Array} selectorsStr
 * @returns {Array} Devuelve el array de selectores
 * o null si hay error
 */
export const normalizeOrderSelectors = (selectorsStr) => {
  if (!selectorsStr || !Array.isArray(selectorsStr)) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'normalizeOrderSelectors',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'No existe selectorsStr' });
    return null;
  }
  const returnOptions = [];
  selectorsStr.forEach((eachStr) => {
    if (eachStr.includes('span')) {
      const doc = new DOMParser().parseFromString(
        `<div> ${eachStr} </div>`,
        'text/html'
      );

      const a = Array.from(doc.querySelectorAll('span')).map(eachElement => ({
        weight: parseInt(eachElement.getAttribute('pac-aggr-weight'), 10),
        text: eachElement.innerHTML,
      }));
      returnOptions.push(...a);
    } else {
      const a = {
        weight: 0,
        text: eachStr,
      };
      returnOptions.push(a);
    }
  });

  return returnOptions;
};

/**
 * Normalizamos el selector de los productos
 * @param {string} selectorStr
 * @returns {string} Devuelve string html para pintar el selector
 */
export const normalizeProductSelector = (selectorStr) => {
  if (!selectorStr) {
    const context = createSondaContext('particulares');
    const sonda = new Sonda({
      processName: 'normalizeProductSelector',
      context,
      filePath: 'utils/productUtils',
    });
    sonda.errorMessage({ info: 'No existe selectorStr' });
    return null;
  }
  const doc = new DOMParser().parseFromString(
    `<div> ${selectorStr} </div>`,
    'text/html'
  );

  return doc.querySelector('span').innerHTML;
};

/**
 * Comprobamos si el cliente tiene terminal
 * @param {Object} clientProductsInfo
 * @returns {boolean} Devuelve si tiene terminal o no
 * false en caso de error
 */
export const checkClientHaveDemand = (demands, numberOfDemannd) => {
  if (demands) {
    const hasDemandsChoices = demands.choices !== null;
    if (hasDemandsChoices) {
      const demandsChoices = demands.choices;
      if (Array.isArray(demandsChoices) && demandsChoices.length > 0) {
        let hasDemand = false;
        demandsChoices.forEach((demandChoice) => {
          const { subType } = demandChoice;
          if (subType === numberOfDemannd) {
            hasDemand = true;
          }
        });
        return hasDemand;
      }
    } else {
      return false;
    }
  }
  return false;
};


/**
 * Función que obtiene los summariescommerInfo de todos los commerinfo de un producto.
 * @param {object} commerInfoBlock bloque de todos los commerinfo de un producto
 * @returns {arr} todos los commerInfo de un bloque de commerInfo de un producto
 */
export const getSummariesOfCommerInfo = (commerInfoBlock) => {
  return commerInfoBlock
    .filter(eachFeature => !eachFeature.alias.includes('ic_legales'))
    .reduce((arr, currentCommerInfo) => {
      const aggrList = currentCommerInfo.commerInfo
        && currentCommerInfo.commerInfo
          .filter(aggr => aggr.summary !== '')
          .map(aggr => aggr.summary);
      if (Array.isArray(aggrList) && aggrList.length) {
        arr = /* arr.push([...aggrList]); */ [...arr, ...aggrList];
      }
      return arr;
    }, []);
};
/**
 * Funcion que obtiene los pesos del html embebido en  los commerInfoSummaries de un producto.
 * @param {object} summaries todos los commerInfo de un bloque de commerInfo de un producto.
 * @param {string} aliasProduct alias del producto.
 * @returns {object} objeto con los atributos de alias, el peso,
 * y el texto de la etiqueta html embebida desde contribucion.
 */
export const normalizeSummaryWithWeightToDic = (summaries, aliasProduct) => {
  return summaries.reduce((obj, currentSummary) => {
    const summaryReplaced = currentSummary.replaceAll(/•/gi);
    const doc = new DOMParser().parseFromString(
      `<div> ${summaryReplaced} </div>`,
      'text/html'
    );

    const summariesNormalized = Array.from(doc.querySelectorAll('span')).map(
      eachElement => ({
        alias: eachElement.getAttribute('pac-aggr-alias'),
        weight: parseInt(eachElement.getAttribute('pac-aggr-weight'), 10),
        text: eachElement.innerHTML,
      })
    );

    for (const eachSummary of summariesNormalized) {
      if (
        eachSummary
        && eachSummary.alias
        && eachSummary.text
        && eachSummary.weight
        && eachSummary.alias !== 'aggr_tv_canales'
      ) {
        if (eachSummary.alias.includes(' ')) {
          const aliases = eachSummary.alias.split(' ');
          for (const alias of aliases) {
            obj[alias] = eachSummary.weight;
          }
        } else {
          obj[eachSummary.alias] = eachSummary.weight;
        }
      }
    }
    obj[aliasProduct] = aliasProduct;
    return obj;
  }, {});
};

/**
 * Comparador para ver si el producto1 es de mas valor que el producto2
 * y comprobar si es un downgrade o un upgrade, de forma generica para cualquier producto.
 * @param {obejct} fisrtProduct producto target sobre el que comaprar el producto2.
 * @param {obejct} secondProduct producto origen sobre el que se compara.
 * @returns {boolean} true si es upgrade o false si es downgrade.
 */
export const compareIfFirstProductGreaterThanSecond = (
  fisrtProduct,
  secondProduct
) => {
  let firstProductPrice;
  let secondProductPrice;
  let isGreater = false;
  if (fisrtProduct && secondProduct) {
    if (
      fisrtProduct.dataMap
      && secondProduct.dataMap
      && (fisrtProduct.dataMap.pvpTachado || fisrtProduct.dataMap.pvp)
      && (secondProduct.dataMap.pvpTachado || secondProduct.dataMap.pvp)
    ) {
      firstProductPrice = fisrtProduct.dataMap.pvpTachado || fisrtProduct.dataMap.pvp;
      secondProductPrice = secondProduct.dataMap.pvpTachado || secondProduct.dataMap.pvp;
      if (
        Number.parseInt(firstProductPrice, 10)
        >= Number.parseInt(secondProductPrice, 10)
      ) {
        isGreater = true;
      } else {
        isGreater = false;
      }
    }
  }
  return isGreater;
};

/**
 * Funcion para obtener el producto seleccionado de un grupo de productos.
 * @param {object} group diccionario de todos los productos de un grupo
 * @returns {object} producto seleccionado si existe.
 */
export const getSelectedProductOfGroup = (group) => {
  let aliasProduct = '';

  if (group) {
    Object.keys(group).forEach((productKey) => {
      const productObject = group[productKey];
      if (productObject.selected) {
        aliasProduct = productKey;
      }
    });
  }
  return aliasProduct;
};

/**
 * Funcion para obtener los parametros y la modal configurada para el me interesa del downgrade.
 * @param {boolean} isDowngrade valor qe nos indica si es un downgrade
 * la configuracion del modal.
 * @param {*} onclicFunction funcion para ejecutar en caso que la
 * operacion sea de activar el modal de retencion.
 * @param {*} urlProdCompra url para redirigir al proceso de compra
 * en caso de que no se tenga que activar el modal.
 * @param {*} pidUrl pid para construir la url dependiendo si llegamos
 * desde el me interesa del flyout o desde la modal deretencion.
 * @param {string} variant variante del producto: contratado, recomendado....
 * @returns
 */
export const getReadyParamsDependsOnDowngrade = (
  isDowngrade,
  onclicFunction,
  urlProdCompra,
  pidUrl,
  variant
) => {
  let resLink;
  let resOnClick;
  if (isDowngrade && variant !== VARIANT.RECOMMENDED) {
    resLink = '';
    resOnClick = onclicFunction;
  } else {
    resLink = `${urlProdCompra}&pid=${pidUrl}`;
    resOnClick = null;
  }
  return { link: resLink, onClick: resOnClick };
};


export const getPrincipalLineProductIdFromMyProducts = (myHiredProducts) => {
  let productIdToReturn = '';
  if (
    myHiredProducts
    && Array.isArray(myHiredProducts)
    && myHiredProducts.length > 0
  ) {
    const myHiredProduct = myHiredProducts[0];
    const hasMyHiredMobile = myHiredProduct.mobile !== null;
    if (hasMyHiredMobile) {
      const myHiredMobile = myHiredProduct.mobile;
      const hasMyHiredlinesMobile = myHiredMobile.linesMobile !== null;
      if (hasMyHiredlinesMobile) {
        const myHiredlinesMobile = myHiredMobile.linesMobile;
        if (Array.isArray(myHiredlinesMobile)) {
          const { productId } = myHiredlinesMobile[0];
          productIdToReturn = productId;
        }
      }
    }
  }
  return productIdToReturn;
};


export const getFusionProductIdFromMyProducts = (myHiredProducts) => {
  let productIdToReturn = '';
  if (
    myHiredProducts
    && Array.isArray(myHiredProducts)
    && myHiredProducts.length > 0
  ) {
    const myHiredProduct = myHiredProducts[0];
    const { productId } = myHiredProduct;
    productIdToReturn = productId;
  }
  return productIdToReturn;
};
